import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Empty,
  Icon,
  Input,
  Modal,
  Pagination,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Text,
  useToast,
} from "@app/ui";
import { createProductSchema } from "@app/core/schemas";
import { createProduct, listProducts } from "../api/dpp.js";
import { RequirePermission } from "../components/RequirePermission.js";
import {
  CardGrid,
  CardGridSkeleton,
  EntityCard,
  ListToolbar,
  useViewMode,
  ViewModeSwitch,
} from "../components/ListView.js";
import { ErrorAlert, useFormErrors } from "../errors/index.js";
import { useLang, useT, useFormatDateTime } from "../i18n.js";
import { NoOrgState } from "../orgs/NoOrgState.js";
import { useOrgs } from "../orgs/OrgContext.js";
import { schemaOptions } from "../dpp/schema-registry.js";
import { ExcelImportModal } from "../dpp/ExcelImportModal.js";
import type { Product, ProductStatus } from "../types.js";

const PAGE_SIZE = 20;

const STATUS_COLOR: Record<ProductStatus, "default" | "success" | "warning"> = {
  draft: "warning",
  active: "success",
  archived: "default",
};

export function ProductsPage() {
  const t = useT();
  const { lang } = useLang();
  const fmtDate = useFormatDateTime();
  const toast = useToast();
  const navigate = useNavigate();
  const { org, loading: orgsLoading } = useOrgs();

  const [items, setItems] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [view, setView] = useViewMode("products");

  // Create modal state
  const [open, setOpen] = useState(false);
  const [sku, setSku] = useState("");
  const [name, setName] = useState("");
  const [gtin, setGtin] = useState("");
  const [schemaId, setSchemaId] = useState("textile-v1");
  const [saving, setSaving] = useState(false);
  const createErrs = useFormErrors();
  const [excelOpen, setExcelOpen] = useState(false);
  // Bumped after an Excel import saves, to refetch the current page.
  const [reloadKey, setReloadKey] = useState(0);

  // Server-side list: debounce typing so each keystroke doesn't hit the API.
  useEffect(() => {
    if (!org) return;
    const orgId = org.id;
    const timer = setTimeout(() => {
      setLoading(true);
      setError(null);
      listProducts(orgId, {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        ...(search.trim() ? { search: search.trim() } : {}),
      })
        .then((res) => {
          setItems(res.items);
          setTotal(res.total);
        })
        .catch((e) => setError(e))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [org?.id, page, search, reloadKey]);

  const schemaLabel = useMemo(() => {
    const map = new Map(schemaOptions(lang).map((o) => [o.value, o.label]));
    return (id: string) => map.get(id) ?? id;
  }, [lang]);

  if (orgsLoading) return <Spin />;
  if (!org) return <NoOrgState />;

  const openCreate = () => {
    setSku("");
    setName("");
    setGtin("");
    createErrs.reset();
    setOpen(true);
  };

  const submitCreate = async () => {
    // Same schema the API enforces — invalid SKU/GTIN is caught before the request.
    const parsed = createErrs.validate(createProductSchema, {
      sku: sku.trim(),
      name: name.trim(),
      ...(gtin.trim() ? { gtin: gtin.trim() } : {}),
      schemaId,
    });
    if (!parsed.ok) return;
    setSaving(true);
    try {
      const created = await createProduct(org.id, parsed.data);
      toast.success(t("products.created"));
      setOpen(false);
      navigate(`/products/${created.id}`);
    } catch (e) {
      createErrs.handle(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title={t("products.title")}
      extra={
        <Space size="sm">
          <RequirePermission permission="import:run">
            <Button size="sm" icon={<Icon name="upload" size={14} />} onClick={() => setExcelOpen(true)}>
              {t("products.excelImport")}
            </Button>
          </RequirePermission>
          <RequirePermission permission="product:write">
            <Button variant="primary" size="sm" icon={<Icon name="plus" size={14} />} onClick={openCreate}>
              {t("products.new")}
            </Button>
          </RequirePermission>
        </Space>
      }
    >
      {error != null && <ErrorAlert error={error} />}

      <ListToolbar
        filters={
          <div style={{ width: 320, maxWidth: "100%" }}>
            <Input
              prefix={<Icon name="search" size={14} />}
              placeholder={t("products.searchPlaceholder")}
              aria-label={t("products.searchPlaceholder")}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
        }
        right={
          <>
            {/* aria-live: after a search the count is the only feedback that the
                list changed, so screen readers get told without a focus move. */}
            <Text small secondary aria-live="polite">
              {t("common.totalCount", { count: total })}
            </Text>
            <ViewModeSwitch value={view} onChange={setView} />
          </>
        }
      />

      {!loading && items.length === 0 ? (
        <Empty description={search.trim() ? t("common.noSearchResults") : t("products.empty")}>
          {search.trim() ? (
            <Button
              size="sm"
              variant="subtle"
              onClick={() => {
                setSearch("");
                setPage(1);
              }}
            >
              {t("common.clearFilters")}
            </Button>
          ) : (
            <RequirePermission permission="product:write">
              <Button variant="primary" size="sm" icon={<Icon name="plus" size={14} />} onClick={openCreate}>
                {t("products.new")}
              </Button>
            </RequirePermission>
          )}
        </Empty>
      ) : view === "list" ? (
        <Table
          rowKey={(p) => p.id}
          loading={loading}
          empty={t("products.empty")}
          onRowClick={(p) => navigate(`/products/${p.id}`)}
          columns={[
            {
              key: "name",
              title: t("products.name"),
              // Name is the identity; the SKU rides under it so the row still
              // answers "which record is this?" without a second column to scan.
              render: (p) => (
                <div className="min-w-0">
                  <div className="truncate font-medium text-foreground">{p.name}</div>
                  <div className="truncate font-mono text-xs text-muted-foreground">{p.sku}</div>
                </div>
              ),
            },
            {
              key: "gtin",
              title: t("products.gtin"),
              hideOnMobile: true,
              render: (p) =>
                p.gtin ? (
                  <span className="font-mono text-xs">{p.gtin}</span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                ),
            },
            {
              key: "schema",
              title: t("products.schema"),
              hideOnMobile: true,
              render: (p) => <span className="text-muted-foreground">{schemaLabel(p.schemaId)}</span>,
            },
            {
              key: "status",
              title: t("common.status"),
              width: 120,
              render: (p) => <Tag color={STATUS_COLOR[p.status]}>{t(`productStatus.${p.status}`)}</Tag>,
            },
            {
              key: "updatedAt",
              title: t("common.updatedAt"),
              width: 170,
              hideOnMobile: true,
              render: (p) => <span className="text-muted-foreground">{fmtDate(p.updatedAt)}</span>,
            },
          ]}
          data={items}
        />
      ) : loading ? (
        <CardGridSkeleton />
      ) : (
        <CardGrid>
          {items.map((p) => (
            <EntityCard
              key={p.id}
              onOpen={() => navigate(`/products/${p.id}`)}
              openLabel={p.name}
              title={p.name}
              code={p.sku}
              status={<Tag color={STATUS_COLOR[p.status]}>{t(`productStatus.${p.status}`)}</Tag>}
              meta={[
                { key: "gtin", label: t("products.gtin"), value: p.gtin ?? "—" },
                { key: "schema", label: t("products.schema"), value: schemaLabel(p.schemaId) },
              ]}
              footer={`${t("common.updatedAt")}: ${fmtDate(p.updatedAt)}`}
            />
          ))}
        </CardGrid>
      )}

      {total > PAGE_SIZE && (
        <div style={{ marginTop: "var(--az-sp-4)" }}>
          <Pagination total={total} pageSize={PAGE_SIZE} current={page} onChange={setPage} />
        </div>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={t("products.createTitle")}
        okText={t("common.create")}
        cancelText={t("common.cancel")}
        onOk={() => void submitCreate()}
        confirmLoading={saving}
      >
        <Space direction="vertical" size="md" style={{ width: "100%" }}>
          {createErrs.formError && (
            <ErrorAlert error={createErrs.formError} onRetry={() => void submitCreate()} />
          )}
          <Input
            name="sku"
            label={t("products.sku")}
            required
            value={sku}
            error={createErrs.fieldErrors.sku}
            onChange={(e) => {
              setSku(e.target.value);
              createErrs.clearField("sku");
            }}
            autoFocus
          />
          <Input
            name="name"
            label={t("products.name")}
            required
            value={name}
            error={createErrs.fieldErrors.name}
            onChange={(e) => {
              setName(e.target.value);
              createErrs.clearField("name");
            }}
          />
          <Input
            name="gtin"
            label={t("products.gtin")}
            help={t("products.gtinHelp")}
            value={gtin}
            error={createErrs.fieldErrors.gtin}
            onChange={(e) => {
              setGtin(e.target.value);
              createErrs.clearField("gtin");
            }}
          />
          <Select
            label={t("products.schema")}
            options={schemaOptions(lang)}
            value={schemaId}
            onChange={setSchemaId}
          />
        </Space>
      </Modal>
      <ExcelImportModal
        orgId={org.id}
        open={excelOpen}
        onClose={() => setExcelOpen(false)}
        onSaved={() => setReloadKey((k) => k + 1)}
      />
    </Card>
  );
}
